import type { KnockGuideStep } from "@knocklabs/client";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logError } from "@src/appV2/lib/analytics/log";
import { openInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";

import { type GuideButtonAction, type GuideButtonContent } from "../types";

export interface HandleGuideActionParams {
  action?: GuideButtonAction;
  content: GuideButtonContent;
  key: string;
  navigate: (path: string) => void;
  step: KnockGuideStep;
}

export async function handleGuideAction(params: HandleGuideActionParams): Promise<void> {
  const { action, content, key, step, navigate } = params;

  if (!content.action) {
    return;
  }

  step.markAsInteracted({
    metadata: {
      action: content.action,
      ...action?.metadata,
    },
  });
  action?.onClick?.(content);

  if (isAbsoluteUrl(content.action)) {
    try {
      await openInBrowser({ url: content.action });
    } catch (error) {
      logError(APP_V2_APP_EVENTS.KNOCK_BROWSER_OPEN_FAILED, { error });
    }

    return;
  }

  if (isRelativePath(content.action)) {
    navigate(content.action);
    return;
  }

  if (/^(mailto|tel|sms):/.test(content.action)) {
    globalThis.location.href = content.action;
    return;
  }

  if (!action?.onClick) {
    logError(APP_V2_APP_EVENTS.KNOCK_GUIDE_ACTION_NOT_HANDLED, {
      error: "Guide action not handled",
      metadata: {
        action: content.action,
        key,
      },
    });
  }
}

function isAbsoluteUrl(value: string) {
  try {
    const url = new URL(value);
    return Boolean(url.protocol && url.host);
  } catch {
    return false;
  }
}

function isRelativePath(value: string): boolean {
  return value.startsWith("/");
}
